import React, { createContext, useContext, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { fetchDeliveries } from '../../../../features/deliveries/deliveryActions';
import { getLocalTodayString } from '../../../../components/CreationDate';
import { isDeliveryDate } from '../utils/deliveryDates';

const DeliveryContext = createContext(null);
export function DeliveryProvider({ children }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const dateParam = params.get('date');
  const selectedDate = isDeliveryDate(dateParam) ? dateParam : getLocalTodayString();
  const list = useSelector(state => state.deliveries.byDate[selectedDate]);
  const byId = useSelector(state => state.deliveries.byId);
  const deliveries = useMemo(() => (list?.ids || []).map(id => byId[id]?.data).filter(Boolean)
    .sort((a, b) => new Date(b.deliveryDateTime) - new Date(a.deliveryDateTime) || b.id - a.id), [list?.ids, byId]);
  useEffect(() => {
    const request = dispatch(fetchDeliveries(selectedDate));
    return () => request.abort();
  }, [dispatch, selectedDate]);
  const setSelectedDate = date => {
    if (!isDeliveryDate(date)) return;
    setParams(previous => { const next = new URLSearchParams(previous); next.set('date', date); return next; });
  };
  const stats = useMemo(() => ({
    total: deliveries.length,
    submitted: deliveries.filter(delivery => delivery.status === 'SUBMITTED').length,
    pending: deliveries.filter(delivery => delivery.status === 'DRAFT').length,
    issues: deliveries.filter(delivery => delivery.outcome === 'REJECTED' || delivery.itemResults.some(result => result.status === 'ISSUE')).length,
  }), [deliveries]);
  return <DeliveryContext.Provider value={{
    selectedDate, setSelectedDate, deliveries, stats,
    loading: !list || list.loading, error: list?.error,
    refresh: () => dispatch(fetchDeliveries(selectedDate)),
    onView: delivery => navigate(`/checks/delivery-check/${delivery.id}`),
    newDelivery: () => navigate(`/checks/delivery-check/create?date=${selectedDate}`),
  }}>{children}</DeliveryContext.Provider>;
}
export function useDelivery() {
  const context = useContext(DeliveryContext);
  if (!context) throw new Error('useDelivery must be used inside DeliveryProvider');
  return context;
}
